// src/hooks/useLearningAreas.ts
//
// Convenience hook returning the canonical Learning Areas list for dropdowns,
// filters and groupings. Prefers the live, editable `curriculum_areas` list from
// SchoolConfigContext; falls back to the built-in constant before config loads.
//
// Use this everywhere a list of learning areas is presented so every surface
// (grid editor, reports, staff filters, intention forms) shows the SAME list.

import { useMemo } from 'react';
import { useSchoolConfigSafe } from '../contexts/SchoolConfigContext';
import {
  CANONICAL_LEARNING_AREAS,
  CANONICAL_LEARNING_AREA_NAMES,
} from '../constants/learningAreas';

export interface LearningAreaOption {
  code: string;
  name: string;
  color?: string;
}

/**
 * Returns the canonical learning areas (active only), plus a plain array of
 * their names, sourced from school configuration with a static fallback.
 */
export function useLearningAreas(): {
  learningAreas: LearningAreaOption[];
  learningAreaNames: string[];
} {
  const { curriculumAreas } = useSchoolConfigSafe();

  return useMemo(() => {
    const active = (curriculumAreas || []).filter((a) => a.isActive);
    if (active.length === 0) {
      return {
        learningAreas: CANONICAL_LEARNING_AREAS.map((a) => ({ ...a })),
        learningAreaNames: [...CANONICAL_LEARNING_AREA_NAMES],
      };
    }
    const learningAreas = active.map((a) => ({
      code: a.code,
      name: a.name,
      color: a.color,
    }));
    return {
      learningAreas,
      learningAreaNames: learningAreas.map((a) => a.name),
    };
  }, [curriculumAreas]);
}
